import React from "react";
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Box,
  IconButton,
  Switch,
  Tooltip,
} from "@mui/material";
import {
  Search as SearchIcon,
  Check,
  Store as StoreIcon,
} from "@mui/icons-material";
import { Vehicle } from "../../../../../api/vehiclesApi";
import { TrustedShopsList } from "./TrustedShopsList";
import { GoogleShopsList } from "./GoogleShopsList";
import { GooglePlaceWithDistance } from "../../../../../types/googlePlaces";
import { Shop } from "../../../../../types/shop";

interface ClosestShopsCardProps {
  selectedVehicle: Vehicle;
  closestShops: Array<{
    shop: any;
    aerialDistance: number;
    distanceText: string;
    drivingDistance?: string;
    drivingDuration?: string;
  }>;
  isLoading: boolean;
  isCalculatingDriving?: boolean;
  error: string | null;
  // Google shops props
  onGoogleShopSelect?: (shops: any) => void;
  // Shop markers toggle
  showShops?: boolean;
  onShopsToggle?: () => void;
  // Map panning
  onPanToShops?: () => void;
  onCenterShop?: (shop: any) => void;
  onPanOut?: () => void;
  onAddShop?: (shop: Shop, preference: number) => void;
  // Google shops state (lifted up from GoogleShopsList)
  googleShops: GooglePlaceWithDistance[];
  setGoogleShops: React.Dispatch<
    React.SetStateAction<GooglePlaceWithDistance[]>
  >;
  convertedShops: Shop[];
  setConvertedShops: React.Dispatch<React.SetStateAction<Shop[]>>;
  isGoogleLoading: boolean;
  setIsGoogleLoading: React.Dispatch<React.SetStateAction<boolean>>;
  googleError: string | null;
  setGoogleError: React.Dispatch<React.SetStateAction<string | null>>;
  hasSearched: boolean;
  setHasSearched: React.Dispatch<React.SetStateAction<boolean>>;
  // Active tab state
  activeTab: "trusted" | "google";
  setActiveTab: React.Dispatch<React.SetStateAction<"trusted" | "google">>;
}

export const ClosestShopsCard: React.FC<ClosestShopsCardProps> = ({
  selectedVehicle,
  closestShops,
  isLoading,
  isCalculatingDriving = false,
  error,
  onGoogleShopSelect,
  showShops = false,
  onShopsToggle,
  onPanToShops,
  onCenterShop,
  onPanOut,
  onAddShop,
  // Google shops state
  googleShops,
  setGoogleShops,
  convertedShops,
  setConvertedShops,
  isGoogleLoading,
  setIsGoogleLoading,
  googleError,
  setGoogleError,
  hasSearched,
  setHasSearched,
  // Active tab state
  activeTab,
  setActiveTab,
}) => {
  return (
    <Card
      sx={{
        width: "100%", // Full width
        boxShadow: 1,
        borderRadius: 1,
        bgcolor: "transparent",
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <CardHeader
        sx={{ pb: 0.75, px: 1, pt: 1 }}
        title={
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            {/* Title */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="subtitle2" fontWeight="medium">
                Nearby Shops
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                backgroundColor: "background.paper",
                borderRadius: 1,
                border: 1,
                borderColor: "divider",
                p: 0.125,
                gap: 0.125,
                width: "fit-content",
              }}
            >
              <Tooltip title="Trusted partner shops">
                <span>
                  <IconButton
                    size="small"
                    aria-label="Trusted partner shops"
                    onClick={() => setActiveTab("trusted")}
                    disabled={closestShops.length === 0}
                    sx={{
                      borderRadius: 0.75,
                      border: 1,
                      borderColor: "transparent",
                      width: 28,
                      height: 28,
                      bgcolor:
                        activeTab === "trusted"
                          ? "warning.main"
                          : "transparent",
                      color:
                        activeTab === "trusted" ? "white" : "text.secondary",
                      opacity: closestShops.length === 0 ? 0.5 : 1,
                      "&:hover": {
                        bgcolor:
                          activeTab === "trusted"
                            ? "warning.dark"
                            : "warning.light",
                        color: "white",
                      },
                      "&:disabled": {
                        color: "text.disabled",
                      },
                    }}
                  >
                    <Check fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>

              <Tooltip title="Search Google Places">
                <IconButton
                  size="small"
                  aria-label="Search Google Places"
                  onClick={() => setActiveTab("google")}
                  sx={{
                    borderRadius: 0.75,
                    border: 1,
                    borderColor: "transparent",
                    width: 28,
                    height: 28,
                    bgcolor:
                      activeTab === "google" ? "primary.main" : "transparent",
                    color: activeTab === "google" ? "white" : "text.secondary",
                    "&:hover": {
                      bgcolor:
                        activeTab === "google"
                          ? "primary.dark"
                          : "primary.light",
                      color: "white",
                    },
                  }}
                >
                  <SearchIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            {onShopsToggle && (
              <Tooltip title="Show shops on map">
                <Box
                  component="span"
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.25,
                    px: 0.5,
                    borderRadius: 1,
                    border: 1,
                    borderColor: showShops ? "warning.main" : "divider",
                    bgcolor: showShops ? "warning.light" : "background.paper",
                  }}
                >
                  <StoreIcon
                    sx={{
                      fontSize: "0.9rem",
                      color: showShops ? "warning.dark" : "text.secondary",
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      color: showShops ? "warning.dark" : "text.secondary",
                      fontWeight: 600,
                      lineHeight: 1,
                      whiteSpace: "nowrap",
                    }}
                  >
                    Show
                  </Typography>
                  <Switch
                    size="small"
                    checked={showShops}
                    onChange={onShopsToggle}
                    inputProps={{ "aria-label": "Show shops on map" }}
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": {
                        color: "warning.main",
                        "& + .MuiSwitch-track": {
                          backgroundColor: "warning.main",
                        },
                      },
                    }}
                  />
                </Box>
              </Tooltip>
            )}
          </Box>
        }
      />

      <CardContent sx={{ pt: 0, px: 1, pb: 0, "&:last-child": { pb: 0 } }}>
        {/* Trusted Shops Tab */}
        {activeTab === "trusted" && (
          <TrustedShopsList
            closestShops={closestShops}
            isLoading={isLoading}
            isCalculatingDriving={isCalculatingDriving}
            error={error}
            onCenterShop={onCenterShop}
          />
        )}

        {/* Google Results Tab */}
        {activeTab === "google" && (
          <GoogleShopsList
            selectedVehicle={{
              lastLatitude: selectedVehicle.lastLatitude || undefined,
              lastLongitude: selectedVehicle.lastLongitude || undefined,
            }}
            onShopSelect={onGoogleShopSelect}
            onCenterShop={onCenterShop}
            onPanOut={onPanOut}
            onAddShop={onAddShop}
            // Pass the lifted state
            googleShops={googleShops}
            setGoogleShops={setGoogleShops}
            convertedShops={convertedShops}
            setConvertedShops={setConvertedShops}
            isGoogleLoading={isGoogleLoading}
            setIsGoogleLoading={setIsGoogleLoading}
            googleError={googleError}
            setGoogleError={setGoogleError}
            hasSearched={hasSearched}
            setHasSearched={setHasSearched}
          />
        )}
      </CardContent>
    </Card>
  );
};
